import { YouTube } from "@mui/icons-material";
import { Box, Button, Card, Chip, Typography, useColorScheme } from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import HydariaElement from "../assets/Realms/Hydaria.webp";
import MigoraElement from "../assets/Realms/Migora.webp";

export type AboutText = "default" | "Aly" | "Mostafa";

interface AboutUsProps {
  accentColor?: string;
}

export default function AboutUs({ accentColor = "rgba(120, 119, 198, 0.8)" }: AboutUsProps) {
  const [textVersion, setTextVersion] = useState<AboutText>("default");
  const { mode } = useColorScheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = mode === "dark";

  // Extract RGB values from rgba string to create different opacity variants
  const rgbMatch = accentColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  const rgb = rgbMatch ? `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}` : "120, 119, 198";

  return (
    <Box
      sx={{
        py: 8,
        px: { xs: 2, sm: 4, md: 6 },
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          position: "relative",
        }}
      >
        {/* Magical glow effect behind title */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "400px",
            height: "100px",
            background: isDark
              ? `radial-gradient(ellipse, rgba(${rgb}, 0.15) 0%, transparent 70%)`
              : `radial-gradient(ellipse, rgba(${rgb}, 0.08) 0%, transparent 70%)`,
            filter: "blur(20px)",
            zIndex: -1,
          }}
        />

        <Typography
          level="h2"
          sx={{
            color: isDark ? "white" : "#1e293b",
            fontWeight: 300,
            letterSpacing: "0.1em",
            textShadow: isDark
              ? `0 0 20px rgba(${rgb}, 0.5)`
              : `0 0 10px rgba(${rgb}, 0.3)`,
            mb: 2,
          }}
        >
          ABOUT THE AUTHORS
        </Typography>

        {textVersion !== "default" && (
          <Button
            variant="outlined"
            onClick={() => setTextVersion("default")}
            sx={{
              background: isDark
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.2)"
                : "1px solid rgba(0, 0, 0, 0.2)",
              color: isDark ? "white" : "#1e293b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.8rem",
              fontFamily: "'Cinzel', serif",
              "&:hover": {
                background: isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            Short Version
          </Button>
        )}

        {/* Decorative elements */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: "60px",
              height: "1px",
              background: isDark
                ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)"
                : "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.3), transparent)",
            }}
          />
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: `radial-gradient(circle, rgba(${rgb}, 0.8) 0%, transparent 70%)`,
              boxShadow: `0 0 10px rgba(${rgb}, 0.6)`,
            }}
          />
          <Box
            sx={{
              width: "60px",
              height: "1px",
              background: isDark
                ? "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)"
                : "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.3), transparent)",
            }}
          />
        </Box>
      </Box>

      {/* Authors Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 4,
          justifyContent: "center",
          mb: 6,
        }}
      >
        {/* Mostafa Card */}
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            maxWidth: "350px",
            background: isDark
              ? "rgba(0, 0, 0, 0.3)"
              : "rgba(255, 255, 255, 0.6)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(10px)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: isDark
                ? "0 12px 24px rgba(0, 0, 0, 0.4)"
                : "0 12px 24px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(255, 215, 0, 0.6)",
            },
          }}
          onClick={() => setTextVersion("Mostafa")}
        >
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                overflow: "hidden",
                border: "3px solid rgba(255, 215, 0, 0.6)",
                boxShadow: "0 4px 16px rgba(255, 215, 0, 0.3)",
                "& img": {
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                },
              }}
            >
              <img src={MigoraElement} alt="Mostafa Elbehery" loading="lazy" />
            </Box>

            <Typography
              level="title-lg"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                letterSpacing: "0.05em",
                textAlign: "center",
              }}
            >
              MOSTAFA A.M. ELBEHERY
            </Typography>

            <Typography
              level="body-md"
              component="a"
              href="mailto:mostafa@timelessaria.com"
              sx={{
                color: "rgba(255, 215, 0, 0.8)",
                textDecoration: "none",
                fontSize: "0.9rem",
                "&:hover": {
                  color: "rgba(255, 215, 0, 1)",
                  textDecoration: "underline",
                },
              }}
            >
              mostafa@timelessaria.com
            </Typography>

            <Typography
              level="body-sm"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(30, 41, 59, 0.6)",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              Click to read Mostafa's story
            </Typography>
          </Box>
        </Card>

        {/* Aly Card */}
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            maxWidth: "350px",
            background: isDark
              ? "rgba(0, 0, 0, 0.3)"
              : "rgba(255, 255, 255, 0.6)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(10px)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: isDark
                ? "0 12px 24px rgba(0, 0, 0, 0.4)"
                : "0 12px 24px rgba(0, 0, 0, 0.15)",
              border: "1px solid rgba(239, 68, 68, 0.6)",
            },
          }}
          onClick={() => setTextVersion("Aly")}
        >
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                overflow: "hidden",
                border: "3px solid rgba(239, 68, 68, 0.6)",
                boxShadow: "0 4px 16px rgba(239, 68, 68, 0.3)",
                "& img": {
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                },
              }}
            >
              <img src={HydariaElement} alt="Aly Said" loading="lazy" />
            </Box>

            <Typography
              level="title-lg"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                letterSpacing: "0.05em",
                textAlign: "center",
              }}
            >
              ALY M.A. SAID
            </Typography>

            <Typography
              level="body-md"
              component="a"
              href="mailto:aly@timelessaria.com"
              sx={{
                color: "rgba(239, 68, 68, 0.8)",
                textDecoration: "none",
                fontSize: "0.9rem",
                "&:hover": {
                  color: "rgba(239, 68, 68, 1)",
                  textDecoration: "underline",
                },
              }}
            >
              aly@timelessaria.com
            </Typography>

            <Typography
              level="body-sm"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(30, 41, 59, 0.6)",
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              Click to read Aly's story
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Content Section */}
      <Card
        variant="outlined"
        sx={{
          background: isDark
            ? "rgba(0, 0, 0, 0.3)"
            : "rgba(255, 255, 255, 0.6)",
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
          backdropFilter: "blur(10px)",
          p: { xs: 3, sm: 4, md: 5 },
        }}
      >
        {textVersion === "default" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                textAlign: "center",
                letterSpacing: "0.02em",
              }}
            >
              Lifelong friends with a passion for storytelling.
            </Typography>

            <Typography
              level="body-lg"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(30, 41, 59, 0.9)",
                lineHeight: 1.8,
                textAlign: "justify",
                maxWidth: "800px",
              }}
            >
              Based out of Germany and Scotland, Mostafa Elbehery & Aly Said
              have been working together on building a massive fantasy world as
              a platform for telling tales of political intrigue, magical
              adventures, and fantastical epics. Inspired by the transformative
              capabilities such stories hold, offering exciting flights of fancy
              into fictional worlds that are conjurable only in one's mind.
            </Typography>

            <Typography
              level="body-lg"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(30, 41, 59, 0.9)",
                lineHeight: 1.8,
                textAlign: "justify",
                maxWidth: "800px",
              }}
            >
              While they have attained Master's Degrees in Computer Science and
              Media Technology, and have written award winning academic papers,
              they have always been enthralled by the world of fantasy. Inspired
              by titans of the genre such as J.R.R Tolkien, George R.R Martin,
              David & Leigh Eddings, amongst numerous others, they have set
              about creating the world of Osgara.
            </Typography>

            <Typography
              level="body-lg"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(30, 41, 59, 0.9)",
                lineHeight: 1.8,
                textAlign: "justify",
                maxWidth: "800px",
              }}
            >
              For which a first series "Timeless' Aria" is being published, the
              first volume "The Sapphire Shore" having been released in November
              of 2023, and the next two volumes completing the trilogy are
              already written and need only to be edited before they can be
              shared with the world.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: 2,
                maxWidth: "800px",
                textAlign: isMobile ? "center" : "justify",
              }}
            >
              <Typography
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(30, 41, 59, 0.9)",
                  lineHeight: 1.8,
                }}
              >
                Going beyond the main series itself, they plan to release
                Novellas on a seasonal basis, the first of which acts as a
                prelude to the main novels and can be found freely above. Going
                forward, these Seasonal Novellas will be published as well, as
                well as a few hand crafted to share details and snippets about
                the world, available on
              </Typography>
              <Chip
                size="md"
                startDecorator={<YouTube />}
                variant="outlined"
                onClick={() =>
                  window.open(
                    "https://www.youtube.com/@TimelessAriaFantasy",
                    "_blank"
                  )
                }
                sx={{
                  background: isDark
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.2)"
                    : "1px solid rgba(0, 0, 0, 0.2)",
                  color: isDark ? "white" : "#1e293b",
                  cursor: "pointer",
                  "&:hover": {
                    background: "rgba(255, 0, 0, 0.1)",
                    border: "1px solid rgba(255, 0, 0, 0.5)",
                  },
                }}
              >
                @TimelessAriaFantasy
              </Chip>
            </Box>
          </Box>
        )}

        {textVersion === "Aly" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                textAlign: "center",
                letterSpacing: "0.02em",
                fontStyle: "italic",
              }}
            >
              "I've always loved storytelling and I've always been a harsh
              critic, hard to please."
            </Typography>

            {[
              "Aly here, my story is a little different, perhaps a bit more arrogant though I do nothing out of arrogance. I've read, seen, heard and sat through many stories, and I've always found ways to poke holes at them. When it came to reading Mostafa's writings, I poked constructively, and when it came time for me to write, I realised my arrogance.",
              "Writing is a wonderful outlet of a variety of emotions, but writing one's heart out is no guarantee for a good story. When it came time for me to write, I struggled with it greatly, not in forming a coherent plot or linguistically sound writing. I struggled with painting the amazing worlds I saw in my minds eye with my words, but I loved stories and I could not stop. So I looked for help. If, in the course of reading our books, you've not noticed two distinct writing styles. That will be success for my imitation skills, for when it came time to ask for help and improve my writing, I went to my best friend.",
              "We've studied together, we've been on countless nonsensical adventures and we've worked together in a start-up of our own. The ride's always been fun, and writing a book together is hopefully one of the many things we'll keep doing together through our journey in life.",
            ].map((paragraph, index) => (
              <Typography
                key={index}
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(30, 41, 59, 0.9)",
                  lineHeight: 1.8,
                  textAlign: "justify",
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>
        )}

        {textVersion === "Mostafa" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                textAlign: "center",
                letterSpacing: "0.02em",
                fontStyle: "italic",
              }}
            >
              "You did what now?" - Mostafa Elbehery 2008
            </Typography>

            {[
              "Mostafa here, I wanted to tell this story from my POV just because we thought it was funnier with context.",
              "Everyone is undoubtedly exceptional in some way, you only have to find it. For me, I thought I had found that in the form of top rankings and exceptional grades at school at a very young age. But everything changed when the fire nation attacked. One new addition to our class, who is now one of my dearest friends, ousted me handily. No amount of studying would see me return to former glory, and yet on one random day we were both asked to participate in a National Short Story Competition. Then, one short pirate-themed tale with a joke treasure later, and the tables were turned.",
              "Fast forward to IGCSE level education and things changed. You see, up until that point our writing tasks were loose enough to allow for creativity, but no longer. IGCSE exams were rigid, and rightly so for what they wished to test for. I, however, had 'no time for that' and so inspired by the single most epic piece of writing I had laid eyes upon at the time, I took it upon myself to create a full fantasy world. (Thank you, Professor Tolkien.) This is 2006 roughly, and youthful exuberance turned out to be one force of nature that I cannot comprehend. I wrote four books in the span of two to three years, and the process was magical. Of course, I made so many obvious mistakes I was oblivious to at the time, but it was magical nontheless. Though I had a problem, I was writing these massive books that no one had the time to read. I was writing to an audience of none. (Bless you mom for finding the time to read the first one!)",
            ].map((paragraph, index) => (
              <Typography
                key={index}
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(30, 41, 59, 0.9)",
                  lineHeight: 1.8,
                  textAlign: "justify",
                }}
              >
                {paragraph}
              </Typography>
            ))}

            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                textAlign: "center",
                letterSpacing: "0.02em",
                fontStyle: "italic",
                mt: 2,
              }}
            >
              Enter, Aly.
            </Typography>

            {[
              "We met during these IGCSE years and quickly became best of friends. We did a whole slew of crazy things that would pass for content creation these days I'm almost sure of it. We rewrote song lyrics, we recorded skits, we got a green blanket and pretended it was a green screen and went filming. We watched Lord of the Rings together and attempted to recrate the charge of the Rohirrim in my back yard. Then I sort of passed him the first book I wrote, and at this point I had given it to multiple people who VERY understandably did not have the time for 600 A4 pages of a 16-Year Old's rambling. So I thought at best he'd give me some feedback, at worst he'd forget about it.",
            ].map((paragraph, index) => (
              <Typography
                key={index}
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(30, 41, 59, 0.9)",
                  lineHeight: 1.8,
                  textAlign: "justify",
                }}
              >
                {paragraph}
              </Typography>
            ))}

            <Typography
              level="h4"
              sx={{
                color: isDark ? "white" : "#1e293b",
                fontWeight: 300,
                textAlign: "center",
                letterSpacing: "0.02em",
                fontStyle: "italic",
                mt: 2,
              }}
            >
              "I read it. Yeah, all of it." - Aly Said 2008
            </Typography>

            {[
              "In one day he read what I wrote in months, he had feedback, he spotted mistakes, and above all else, he was excited to read the rest of it. Book after book he devoured them whole, we discussed them, we dissected them, and we enjoyed them, I now had an audience of one. However, at this point we were gearing up to head into five long years of Engineering Degrees, so nothing serious came of it at the time.",
              "Fast forward one last time and our lives looked far more detached from what we would have imagined them. We were both doing Masters in different countries, and we were adjusting to new norms. Each being in their own world gave us far less time to talk, and it was only ever going to be online. So it was a pleasant surprise when Aly gave me a ten chapter introduction to something he had been creating in our time apart, this came at the very same time I was following lectures by one Brandon Sanderson. The devastating advise of 'Start Over' couldn't have come at a better time.",
              "It was when we finally reunited back in 2017 that I proposed this idea, I vividly remember that fateful car ride too. I mentioned my intention of starting anew, just to see how it would look like to publish a book. Aly was writing his thing at the time too, and we really needed to reconnect over anything, him being in Scotland and me in Germany. So, I floated the idea of using our Computer Science knowledge to write a fully dynamic book. As you can tell from this page, this is not what we ended up doing. But the story from that project is what we chiselled away at to get to Timeless' Aria. And for such an amazing journey as it has been, I truly hope that these are just our first steps, that this is just the beginning. We wrote all three books together, we edited them together, and we fussed over every little detail together. All in what little scraps of time we are able to scrounge from busy working lives.",
            ].map((paragraph, index) => (
              <Typography
                key={index}
                level="body-lg"
                sx={{
                  color: isDark
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(30, 41, 59, 0.9)",
                  lineHeight: 1.8,
                  textAlign: "justify",
                }}
              >
                {paragraph}
              </Typography>
            ))}

            <Typography
              level="body-lg"
              sx={{
                color: isDark
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(30, 41, 59, 0.9)",
                lineHeight: 1.8,
                textAlign: "justify",
                fontStyle: "italic",
              }}
            >
              One of my English Teachers once told me to reach for the stars. I
              wonder now what our outstretched hands might find?
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
}
